import { apiService } from "api/apiService";
import { shouldFetch } from "shared/utils";
import useHelpers from "composables/useHelpers";
import { cloneDeep, merge, random } from "lodash";

const toPixels = (value) => `${value}px`;

// Matrix values
export const MATRIX_COLUMN_WIDTH = 40;
export const MATRIX_COLUMN_WIDTH_IN_PIXELS = toPixels(MATRIX_COLUMN_WIDTH);

export const MATRIX_FIRST_COLUMN_MINWIDTH = 250;
export const MATRIX_FIRST_COLUMN_MINWIDTH_IN_PIXELS = toPixels(
  MATRIX_FIRST_COLUMN_MINWIDTH,
);

export const MATRIX_FIRST_COLUMN_MAXWIDTH = 300;
export const MATRIX_FIRST_COLUMN_MAXWIDTH_IN_PIXELS = toPixels(
  MATRIX_FIRST_COLUMN_MAXWIDTH,
);

// Use this to test the matrix
const TEST_MATRIX_STUDENT_PERCENTILE = false;

export const MATRIX_STUDENT_PERCENTILE_MAP = {
  null: {
    label: "Komt niet voor in dit cluster",
  },
  poor: {
    label: "Nog niet gestart",
  },
  average: {
    label: "In ontwikkeling",
  },
  good: {
    label: "Beheers",
  },
  excellent: {
    label: "Expert",
  },
};

export const MATRIX_STUDENT_PERCENTILE_OPTIONS = Object.keys(
  MATRIX_STUDENT_PERCENTILE_MAP,
);

const MATRIX_DEFAULT_DATA_STUDENT = {
  score: 0,
  max_score: 0,
  percentile: null,
  student_id: null,
  percentage: 0,
};

const DEFAULT_MATRIX_LESSENGROUP_STUDENT = {
  id: null,
  first_name: "",
  last_name: "",
  middle_name: "",
  name: "",
};

const DEFAULT_MATRIX_CLUSTER_PERCENTILES = {
  null: 0,
  poor: 0,
  average: 0,
  good: 0,
  excellent: 0,
};

export const DEFAULT_MATRIX_CLUSTER = {
  id: null,
  lesson_class: null,
  name: "",
  school_year: null,
  students: [],
  percentiles: DEFAULT_MATRIX_CLUSTER_PERCENTILES,
  learning_objectives: [],
};

const DEFAULT_MATRIX_LESSONGROUP = {
  lesson_class: null,
  subject_shortage: null,
  clusters: [],
  students: [],
};

class matrixCluster {
  id;
  lesson_class;
  name;
  school_year;
  students;
  percentiles;
  learning_objectives;

  constructor(data = {}, _sortedClusterStudentIds = []) {
    Object.defineProperty(this, "isFetching", {
      value: { learningObjectives: false },
      writable: true,
      enumerable: false,
    });

    Object.defineProperty(this, "hasFetched", {
      value: { learningObjectives: false },
      writable: true,
      enumerable: false,
    });

    Object.defineProperty(this, "sortedClusterStudentIds", {
      value: _sortedClusterStudentIds,
      writable: true,
      enumerable: false,
    });

    this.id = data.id ?? DEFAULT_MATRIX_CLUSTER.id;
    this.lesson_class =
      data.lesson_class ?? DEFAULT_MATRIX_CLUSTER.lesson_class;
    this.name = data.name ?? DEFAULT_MATRIX_CLUSTER.name;
    this.school_year = data.school_year ?? DEFAULT_MATRIX_CLUSTER.school_year;
    this.learning_objectives =
      data.learning_objectives ?? DEFAULT_MATRIX_CLUSTER.learning_objectives;

    this.init(data);
  }

  init(data = {}) {
    const { students = [] } = data;

    this.percentiles = merge(
      {},
      DEFAULT_MATRIX_CLUSTER_PERCENTILES,
      data.percentiles,
    );

    this.students = this.sortStudents(students);

    if (
      import.meta.env.VITE_APP_ENV === "development" &&
      TEST_MATRIX_STUDENT_PERCENTILE
    ) {
      // We set the percentiles for the cluster based on the students
      Object.keys(this.percentiles).forEach((percentile) => {
        this.percentiles[percentile] = this.students.filter(
          ({ percentile: studentPercentile }) =>
            studentPercentile === percentile,
        ).length;
      });
    }
  }

  sortStudents(students = []) {
    // We use the reduce function to make sure the order of the students is the same as the order of the sortedClusterStudentIds
    // This is important because we want to make sure the students are in the same order as the students in the header
    // And have a default value when the student is not found in the students array
    return this.sortedClusterStudentIds.reduce((acc, studentId) => {
      let student = cloneDeep(MATRIX_DEFAULT_DATA_STUDENT);

      const foundStudent = students.find(
        ({ student_id }) => student_id === studentId,
      );

      if (foundStudent) {
        student = merge({}, student, foundStudent);

        // Here we set some random test values for the student
        if (
          import.meta.env.VITE_APP_ENV === "development" &&
          TEST_MATRIX_STUDENT_PERCENTILE
        ) {
          const options = MATRIX_STUDENT_PERCENTILE_OPTIONS.filter(
            (percentile) => percentile !== "null",
          );

          student.percentage = random(0, 100);

          student.percentile = random(0, 20)
            ? options[Math.floor(Math.random() * options.length)]
            : "null";
        }
      }

      acc.push(student);

      return acc;
    }, []);
  }

  async fetchLearningObjectives() {
    if (
      this.isFetching["learningObjectives"] ||
      !shouldFetch(this.hasFetched["learningObjectives"])
    ) {
      return;
    }

    try {
      this.isFetching["learningObjectives"] = true;

      const { employeeId } = useHelpers();

      const {
        data: { data: learningObjectives = [] },
      } = await apiService.get(
        `api/teacher/${employeeId}/lesson-groups/learning-objectives/cross-table/${this.id}`,
      );

      this.hasFetched["learningObjectives"] = new Date();

      this.setLearningObjectives(learningObjectives);
    } catch (error) {
      console.log(
        "🚀 ~ matrixCluster ~ fetchLearningObjectives ~ error:",
        error,
      );
    } finally {
      this.isFetching["learningObjectives"] = false;
    }
  }

  setLearningObjectives(learningObjectives = []) {
    this.learning_objectives = learningObjectives.map((learningObjective) => {
      learningObjective.students = this.sortStudents(
        learningObjective.students,
      );

      learningObjective.success_criterias =
        learningObjective.success_criterias.map((successCriteria) => {
          successCriteria.students = this.sortStudents(
            successCriteria.students,
          );

          return successCriteria;
        });

      return learningObjective;
    });
  }
}

export class matrixLessonClass {
  lesson_class;
  subject_shortage;
  clusters;
  students;

  constructor(data = {}) {
    this.lesson_class =
      data.lesson_class ?? DEFAULT_MATRIX_LESSONGROUP.lesson_class;
    this.subject_shortage =
      data.subject_shortage ?? DEFAULT_MATRIX_LESSONGROUP.subject_shortage;

    this.init(data);
  }

  init({ students = [], clusters = [] } = {}) {
    this.students = students.map((student) =>
      merge({}, DEFAULT_MATRIX_LESSENGROUP_STUDENT, student),
    );

    const sortedClusterStudentIds = students.map(({ id }) => id);

    this.clusters = clusters.map(
      (cluster) => new matrixCluster(cluster, sortedClusterStudentIds),
    );
  }

  get gridRowCount() {
    return this.students.length;
  }
}
